import React from 'react';
import HBDatepicker, { HBDatepickerProps } from '../HBDatepicker/HBDatepicker';
import HBBaseFormField, {
    HBBaseFormFieldProps,
    splitFieldProps
} from '../HBBaseFormField/HBBaseFormField';

export type HBDatepickerFieldProps = Omit<HBBaseFormFieldProps, 'size'> &
    Omit<HBDatepickerProps, 'value'> & {
        id: string;
        /** Ref forwarded to the underlying datepicker input element. */
        inputRef?: React.Ref<HTMLInputElement>;
        /** Selected date value; passed as `selected` to the underlying HBDatepicker. */
        value: Date | null;
        /** The currently selected date shown in the calendar popup; alias for `value` in the underlying react-datepicker. */
        selected?: Date | null;
        /** When true, shows the calendar inline below the input instead of in a popup. */
        inline?: boolean;
        /** Controls the open state of the calendar popup externally. */
        open?: boolean;
        /** Sets the calendar view to this date when opened, without affecting the selected value. */
        date?: Date;
        /** Earliest selectable date; dates before this are disabled in the calendar. */
        minDate?: Date;
        /** Latest selectable date; dates after this are disabled in the calendar. */
        maxDate?: Date;
        /** Fires when the calendar loses focus. */
        onFocus?: React.FocusEventHandler<HTMLElement>;
        /** Fires when a key is pressed while the input has focus. */
        onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
        /** Fires when a date is selected; called after `onChange` with the same date value. */
        onSelect?: (
            date: Date | null,
            event?:
                | React.MouseEvent<HTMLElement>
                | React.KeyboardEvent<HTMLElement>
        ) => void;
        /** HTML `autocomplete` attribute forwarded to the underlying input. */
        autoComplete?: string;
        /** When true, the input is read-only; the calendar popup is still accessible. */
        readOnly?: boolean;
        /** HTML `tabIndex` attribute for keyboard navigation order. */
        tabIndex?: number;
        /** HTML `autoFocus` attribute; focuses the input on mount. */
        autoFocus?: boolean;
        /** HTML `form` attribute associating the input with a form by its id. */
        form?: string;
        /** HTML `title` attribute shown as a browser tooltip on hover. */
        title?: string;
        /** react-datepicker `aria-label` prop forwarded to the input element. */
        ariaLabel?: string;
        /** ARIA attribute identifying the element(s) that describe the input. */
        'aria-describedby'?: string;
        /** ARIA attribute indicating the input has a validation error; set to `"true"` when invalid. */
        'aria-invalid'?: string;
        /** ARIA label for the input when a visible label is not available. */
        'aria-label'?: string;
        /** ARIA attribute pointing to the element(s) that label the input. */
        'aria-labelledby'?: string;
        /** ARIA attribute indicating the field is required. */
        'aria-required'?: string;
        /** Enables DS Next visual styling (updated disabled colors and alignment). @deprecated Will be removed post-migration. */
        isDSNext?: boolean;
    };

const HBDatepickerField = ({ inputRef, ...props }: HBDatepickerFieldProps) => {
    const { fieldProps, inputProps } = splitFieldProps(props);

    return (
        <HBBaseFormField {...fieldProps}>
            <HBDatepicker
                {...inputProps}
                ref={inputRef}
                selected={inputProps.value}
            />
        </HBBaseFormField>
    );
};

export default HBDatepickerField;
